import type { CognitoUserStatus } from '@metron/shared';

/**
 * One status option per `CognitoUserStatus` value, plus `disabled` for
 * `enabled === false` (which wins over whatever `userStatus` says).
 *
 * This module is the single source of truth for the Status column, the Status
 * badge and the Status filter, replacing the five hand-rolled copies that used
 * to live in the two per-page user tables it replaced. There is deliberately
 * **no** catch-all bucket: the filter and the badge read the same map, so a
 * status can never be filterable under one label and rendered under another.
 * A Cognito status outside the union is not relabelled either — it renders
 * verbatim through `rawStatusOption` (MET-3282).
 */
export type UserStatusKey =
  | 'disabled'
  | 'archived'
  | 'compromised'
  | 'unknown'
  | 'reset-required'
  | 'password-reset-required'
  | 'unconfirmed'
  | 'active'
  | 'federated'
  | RawUserStatusKey;

/** The key of a status outside `CognitoUserStatus`: `raw:` + the raw value. */
export type RawUserStatusKey = `raw:${string}`;

/** The `variant` values the shared `Badge` component accepts. */
export type UserStatusBadgeVariant = 'default' | 'info' | 'secondary' | 'destructive' | 'outline';

export interface UserStatusOption {
  /** Stable key. Used as the filter's URL value, so never change these strings. */
  key: UserStatusKey;
  /** Human label. The badge and the filter option render exactly this. */
  label: string;
  /** Badge variant for `<Badge variant={...}>`. */
  variant: UserStatusBadgeVariant;
  /**
   * Sort rank, ascending from "cannot sign in at all" to "fully active".
   *
   * The four ranks that exist today keep their relative order (Disabled <
   * Password Reset Required < Unconfirmed < Active), inherited from the two
   * per-page user tables this module replaced.
   *
   * The four statuses that had no rank of their own are placed by how much
   * stands between the user and a working sign-in: `ARCHIVED` and
   * `COMPROMISED` are terminal (the account is retired or locked for
   * security), `UNKNOWN` is indeterminate, and `RESET_REQUIRED` needs the user
   * to complete a forgot-password flow before they can sign in — one step more
   * than `FORCE_CHANGE_PASSWORD`, where a temporary password already works.
   *
   * `EXTERNAL_PROVIDER` ranks next to `CONFIRMED` at the top: an SSO account
   * signs in as fully as a confirmed one, it just does it elsewhere.
   */
  rank: number;
}

const OPTION_LIST: readonly UserStatusOption[] = [
  { key: 'disabled', label: 'Disabled', variant: 'destructive', rank: 0 },
  { key: 'archived', label: 'Archived', variant: 'secondary', rank: 1 },
  { key: 'compromised', label: 'Compromised', variant: 'destructive', rank: 2 },
  { key: 'unknown', label: 'Unknown', variant: 'secondary', rank: 3 },
  { key: 'reset-required', label: 'Reset Required', variant: 'outline', rank: 4 },
  {
    key: 'password-reset-required',
    label: 'Password Reset Required',
    variant: 'outline',
    rank: 5,
  },
  { key: 'unconfirmed', label: 'Unconfirmed', variant: 'secondary', rank: 6 },
  { key: 'active', label: 'Verified', variant: 'default', rank: 7 },
  { key: 'federated', label: 'Federated', variant: 'info', rank: 8 },
] as const;

/**
 * Every status option, ordered by `rank` ascending — the same order the Status
 * column sorts in, so the filter list and the column agree.
 */
export const USER_STATUS_OPTIONS: readonly UserStatusOption[] = OPTION_LIST;

// Safe: OPTION_LIST has one entry per literal UserStatusKey; `raw:*` keys come from rawStatusOption and are never looked up here.
export const USER_STATUS_OPTIONS_BY_KEY: Readonly<Record<UserStatusKey, UserStatusOption>> = Object
  .fromEntries(
    OPTION_LIST.map((option) => [option.key, option]),
  ) as Record<UserStatusKey, UserStatusOption>;

/** `CognitoUserStatus` → status key, for an enabled user. Exhaustive by type. */
const KEY_BY_COGNITO_STATUS: Readonly<Record<CognitoUserStatus, UserStatusKey>> = {
  UNCONFIRMED: 'unconfirmed',
  CONFIRMED: 'active',
  ARCHIVED: 'archived',
  COMPROMISED: 'compromised',
  UNKNOWN: 'unknown',
  RESET_REQUIRED: 'reset-required',
  FORCE_CHANGE_PASSWORD: 'password-reset-required',
  EXTERNAL_PROVIDER: 'federated',
};

/**
 * The fields `statusKey` needs — anything `CognitoUser`-shaped satisfies it.
 * `userStatus` is widened to `string` on purpose: Cognito can return a status
 * the union does not list yet, and this module has to say so rather than
 * relabel it (MET-3282, where SSO users read "Unknown").
 */
export interface UserStatusInput {
  enabled: boolean;
  userStatus: string;
}

const warnedRawStatuses = new Set<string>();

/**
 * The option for a `userStatus` outside `CognitoUserStatus`: the raw string is
 * the badge text (what the pre-unification tables printed), ranked after every
 * known option, and logged once per distinct value so a new Cognito status is
 * visible instead of disguised. It is not in `USER_STATUS_OPTIONS`, so it never
 * joins the Status filter's vocabulary — such rows show under "All statuses".
 */
function rawStatusOption(userStatus: string): UserStatusOption {
  if (!warnedRawStatuses.has(userStatus)) {
    warnedRawStatuses.add(userStatus);
    console.warn(
      `[user-status] unrecognised Cognito userStatus "${userStatus}"; rendering it verbatim. ` +
        'Add it to CognitoUserStatus and USER_STATUS_OPTIONS.',
    );
  }
  // Ranked after every known option, so raw rows sort together at the end.
  return {
    key: `raw:${userStatus}`,
    label: userStatus,
    variant: 'secondary',
    rank: OPTION_LIST.length,
  };
}

/**
 * The full option (key, label, variant, rank) for a user. `enabled === false`
 * wins over any `userStatus`; a value outside `CognitoUserStatus` gets its own
 * raw option and is never mapped onto one of the known labels.
 */
export function statusOption(user: UserStatusInput): UserStatusOption {
  if (!user.enabled) return USER_STATUS_OPTIONS_BY_KEY.disabled;
  const key: UserStatusKey | undefined =
    KEY_BY_COGNITO_STATUS[user.userStatus as CognitoUserStatus];
  return key === undefined ? rawStatusOption(user.userStatus) : USER_STATUS_OPTIONS_BY_KEY[key];
}

/** The status key for a user. See `statusOption`. */
export function statusKey(user: UserStatusInput): UserStatusKey {
  return statusOption(user).key;
}
